import ReactPDF, { View } from '@react-pdf/renderer';
import React from 'react';
import Dialog from 'src/components/common/Dialog';
import DialogList from 'src/dialogs';
import { Page, Text, Document, StyleSheet } from '@react-pdf/renderer';
import { AnyObject } from 'final-form';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import MathUtil from 'src/util/MathUtil';
import LeaseClauseType from 'src/models/LeaseClauseType';
import LocationModel from 'src/models/leasing/LocationModel';
import ContractAddendumClause from '../redesign/ContractGenerationModels/ContractAddendumClause';
import Lease from 'src/models/leasehold/Lease';
import moment from 'moment';

const styles = StyleSheet.create({
    pageMain: {
        backgroundColor: '#FFFFFF',
        paddingHorizontal: '0.5in',
        paddingLeft: '1.25in',
        paddingRight: '1.25in',
        fontFamily: 'Times-Roman',
    },
    page: {
        backgroundColor: '#FFFFFF',
        paddingTop: '0.54in',
        paddingHorizontal: '0.5in',
        paddingLeft: '0.93in',
        paddingRight: '1.07in',
    },
    pageNumbers: {
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10,
        color: '#8e9094',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
    },
    bold: {
        fontFamily: 'Times-Bold',
    },
    fontNormal: {
        fontWeight: 'normal',
        fontSize: '12pt',
    },
    mt1: {
        marginTop: 10,
    },
    mt2: {
        marginTop: 20,
    },
    mt3: {
        marginTop: 30,
    },
    mt4: {
        marginTop: 40,
    },
    mb1: {
        marginBottom: 10,
    },
    mb2: {
        marginBottom: 20,
    },
    mr1: {
        marginRight: 5,
    },
    mr2: {
        marginRight: 20,
    },
    ml1: {
        marginLeft: 5,
    },
    ml2: {
        marginLeft: 20,
    },
    center: {
        textAlign: 'center',
    },
    text: {
        textAlign: 'justify',
    },
    indent1: {
        textIndent: '.1in',
    },
    indent2: {
        textIndent: '.25in',
    },
    indent3: {
        textIndent: '.125in',
    },
    underlined: {
        textDecoration: 'underline',
    },
    mailingInfo: {
        fontSize: 10,
        fontFamily: 'Times-Roman',
    },
    exhibitHeading: {
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 'bold',
    },
    acknowledgementHeading: {
        textAlign: 'center',
        fontSize: '13pt',
    },
    leaseHeading: {
        textAlign: 'center',
        fontSize: 20,
        fontFamily: 'Times-Bold',
    },
    singleLine: {
        borderTop: '1px solid black',
    },
    doubleLines: {
        borderTop: '1px solid black',
        borderBottom: '1px solid black',
    },
    lineHeight: {
        lineHeight: 1.25,
    },
    h2: {
        fontSize: 12,
    },
    h3: {
        fontSize: '12pt',
    },
    lessorLabel: {
        width: '75px',
        fontSize: 9,
        textAlign: 'right',
        marginRight: '4',
        fontWeight: 'bold',
    },
    lessorData: {
        maxWidth: '20%',
        fontSize: 9,
    },
    footerText: {
        color: 'grey',
        fontSize: 9,
    },
    ownershipColumn: {
        width: '14%',
        fontSize: 9,
        textAlign: 'right',
    },
    bonusColumn: {
        width: '14%',
        fontSize: 9,
        textAlign: 'right',
        paddingRight: 15,
    },
    dudleyInfo: {
        position: 'absolute',
        bottom: '50px',
        left: '70px',
    },
    sbTitle: {
        textAlign: 'center',
        marginBottom: 10,
    },
});

interface IFormonteraMemoProps {
    hideDialog?: () => void;
    // TODO: Update IContract to include these missing fields.
    leaseData?: Lease;
    leaseClauses?: ContractAddendumClause[];
    leaseClauseTypes?: LeaseClauseType[];
    location?: LocationModel;
}

const FormonteraMemo = (props: IFormonteraMemoProps) => {
    const dispatch = useDispatch();
    const isRenewalOption = props?.leaseData?.leasehold?.renewalOption;
    const totalGrossAcres = props?.leaseData?.tracts?.reduce((acc: number, tract: AnyObject) => {
        let acreage = acc;
        if (tract.includeTract === true) {
            let tempGrossAcres = parseFloat(tract.grossAcres) || 0;
            acreage += tempGrossAcres;
        }

        return acreage;
    }, 0);

    const termValDisplay = (mode: string): string => {
        if (mode === 'normal') {
            let primaryTerm: number = !!props?.leaseData ? parseInt(props?.leaseData?.leasehold?.primaryTerm + '', 10) : undefined;
            let primaryTermUnits: string = !!props?.leaseData ? props?.leaseData?.leasehold?.primaryTermUnits : undefined;

            if (!primaryTerm && !primaryTermUnits) {
                return 'zero (0) ';
            }

            let primaryTermString = MathUtil.numberToEnglish(primaryTerm, ' ');
            
            if (primaryTermString.startsWith(' ')) {
                primaryTermString = primaryTermString.replace(' ', '');
            }

            return `${primaryTermString} (${primaryTerm}) `;
        }

        if (mode === 'option') {
            let optionTerm: number = !!props?.leaseData ? parseInt(props?.leaseData?.leasehold?.optionTerm + '', 10) : undefined;
            let optionTermUnits: string = !!props?.leaseData ? props?.leaseData?.leasehold?.optionTermUnits : undefined;
            if (!optionTerm && !optionTermUnits) {
                return 'zero (0) ';
            }

            let optionTermString = MathUtil.numberToEnglish(optionTerm, ' ');

            if (optionTermString.startsWith(' ')) {
                optionTermString = optionTermString.replace(' ', '');
            }

            return `${optionTermString} (${optionTerm}) `;
        }

        return '';
    };

    const displayCounties = () => {
        let counties: Array<string> | undefined = !!props?.location && props?.location?.counties?.filter((c) => !!c);
        let stringCounties: string = '';

        if (!!counties && counties.length > 1) {
            for (let i = 0; i < counties.length; i++) {
                if (!!counties[i] && i !== counties.length - 1) {
                    stringCounties += counties[i] + ', ';
                }
                if (!!counties[i] && i === counties.length - 1) {
                    stringCounties += 'and ' + counties[i] + ' Counties';
                }
            }
            return stringCounties;
        } else if (!!counties && counties.length === 1) {
            return `${counties[0]} County`;
        } else {
            return '';
        }
    };

    return (
        <Dialog title="MEMO" dialogName={DialogList.MEMO_VIEWER} onCloseDialog={() => dispatch(actions.layout.hideDialog())}>
            <ReactPDF.PDFViewer style={{ height: '85vh', width: '70vw' }}>
                <Document>
                    <Page size="LEGAL" style={styles.pageMain}>
                        <View>
                            <View style={{ marginTop: '200px' }}>
                                <Text
                                    style={{
                                        fontSize: '12pt',
                                        textDecorationStyle: 'solid',
                                        textAlign: 'center',
                                        fontFamily: 'Times-Bold',
                                    }}
                                >
                                    MEMORANDUM OF OIL, GAS AND MINERAL LEASE (PAID UP)
                                </Text>
                            </View>
                        </View>

                        <View style={{ marginTop: '15px' }}>
                            <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.lineHeight, styles.indent2]}>
                                In consideration of Ten and no/100's Dollars ($10.00) and other good and valuable 
                                consideration in hand paid, receipt of which is hereby acknowledged, the following lessor 
                                has entered into an Oil, Gas and Mineral Lease with Dudley Land Company, whose address 
                                is 5925 N Robinson Avenue, Oklahoma City, OK 73118, for a term of{' '}
                                <Text style={[styles.bold]}>{termValDisplay('normal')}</Text>{props?.leaseData?.leasehold?.primaryTermUnits}
                                {isRenewalOption ? ` with an option to extend the lease for an additional` : '.' }
                                <Text style={[styles.bold]}>{isRenewalOption ? `${termValDisplay('option')}` : '' }</Text>
                                {isRenewalOption ? `years covering the following described lands located in ${displayCounties()}, North Dakota: ` : '' }
                            </Text>
                        </View>

                        <View style={{ marginTop: '10px'}}>
                            <View>
                                <Text style={[styles.fontNormal, styles.bold, styles.indent1]}>
                                    <Text style={[styles.indent1]}>Lessor:{'                       '}{props?.leaseData?.leasedBy}{', a single person'}{'\n'}</Text>
                                    <Text style={[styles.indent1]}>Date of Lease:{'           '}
                                        {moment(props?.leaseData?.leaseDate).format('MMMM')}{' '}
                                        {moment(props?.leaseData?.leaseDate).format('DD')}{', '}
                                        {moment(props?.leaseData?.leaseDate).format('YYYY')}{'\n'}</Text>
                                    <Text style={[styles.indent1]}>Description:{'              '}{props?.leaseData?.legalDescription}{'\n'}</Text>
                                </Text>
                            </View>
                        </View>

                        <View>
                            <Text style={[styles.fontNormal, styles.lineHeight]}>
                                Containing 
                                <Text style={[styles.bold]}>{' '}{totalGrossAcres.toFixed(2)}{' '}</Text>
                                acres, more or less.
                            </Text>
                        </View>

                        <View style={{ marginTop: '10px'}}>
                            <Text style={[{textAlign: 'justify'}, styles.fontNormal]}>
                            This Memorandum may be executed in any number of counterparts, each of which is deemed
                            an original, and all of which taken together shall constitute one and the same Memorandum.  
                            </Text>
                        </View>

                        <View style={{ marginTop: '20px'}}>
                            <Text style={[styles.fontNormal]}>
                                <Text style={[styles.bold]}>LESSOR:{'\n\n'}</Text>
                                <Text>_____________________________{'\n'}</Text>
                                <Text>{props?.leaseData?.leasedBy}</Text>
                            </Text>
                        </View>

                        <View style={{ marginTop: '80px'}}>
                            <Text style={[styles.fontNormal]}>
                                <Text>THE STATE OF ______________________{')(\n\n'}</Text> 
                                <Text>COUNTY OF ________________________{')('}</Text>
                            </Text>
                        </View>

                        <View style={{ marginTop: '20px'}}>
                            <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.lineHeight, styles.indent3]}>
                            Before me, the undersigned authority, on this day personally appeared
                            ___________________________________  known to me to be the identical person whose
                            name is subscribed to the foregoing instrument, and acknowledged to me that they executed
                            the same for the purposes and consideration therein expressed.
                            </Text>
                        </View>

                        <View style={{ marginTop: '20px'}}>
                            <Text style={[{textAlign: 'justify'}, styles.fontNormal]}>
                                Given under my hand and seal of office this the ___________day of_____________, A.D.
                                20____.
                            </Text>
                        </View>

                        <View style={{marginLeft: '45%'}}>
                            <Text style={[styles.fontNormal]}>
                                <Text>{'\n\n'}_______________________________________{'\n'}</Text>
                                <Text>{'   \t  '} Notary Public, State of______________</Text>
                            </Text>
                        </View>
                    </Page>
                </Document>
            </ReactPDF.PDFViewer>
        </Dialog>
    );
};

export default FormonteraMemo;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/MemoTemplates/FormonteraMemo.tsx